import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Subject, takeUntil, tap } from 'rxjs';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { TDropdown } from 'src/app/project/components/dropdown/dropdown.consts';
import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { logEventInGTAG } from 'src/app/project/services/analytics/google-analytics';
import {
  EGoogleEventCategory,
  EGoogleEventSavedViews,
} from 'src/app/project/services/analytics/google-analytics.consts';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { SitePointFilterService } from '../../../filters/site-point-filter.service';
import { PreferencesService } from '../../../preferences/preferences-service/preferences.service';
import { SiteOptionsService } from '../../../site/site-options/site-options.service';
import { CustomTableService } from '../../../site/site-table/custom-table/custom-table.service';
import { SiteTableColumnsService } from '../../../site/site-table/site-table-columns.service';
import { SiteTableService } from '../../../site/site-table/site-table.service';
import { checkCustomWorkspaceId } from '../../../workspace/workspace';
import { ESavedView } from '../../models/saved-view.enum';
import { TSavedViews, TViewShort } from '../../models/saved-views.model';
import { SavedViewsOptionsDropdownComponent } from '../../saved-views-options-dropdown/saved-views-options-dropdown.component';
import {
  ESavedViewsOptionsDropdownCallback,
  TSavedViewsOptionsDropdownData,
} from '../../saved-views-options-dropdown/saved-views-options-dropdown.model';
import { SavedViewsService } from '../../saved-views.service';
import { SavedViewsDropdownService } from '../saved-views-dropdown.service';
import { generateViewName } from './utils/generate-view-name';

@Component({
  selector: 'pp-saved-views-dropdown-row',
  templateUrl: './saved-views-dropdown-row.component.html',
  styleUrls: ['./saved-views-dropdown-row.component.scss'],
  standalone: false,
})
export class SavedViewsDropdownRowComponent implements OnInit, AfterViewInit {
  @Input() ppSecondaryDropdownService: DropdownService;
  @Input() ppView: TViewShort;
  @Input() ppType: ESavedView;
  @Input() ppViews: TSavedViews;
  @ViewChild('nameInput', { static: false }) nameInput: ElementRef<HTMLInputElement>;

  EIconPath = EIconPath;
  lastKey: string | null = null;

  secondaryDropdown: TDropdown;
  dropdownVisible = false;
  renamingView = false;
  selectedViewIds: {
    [workspaceId: string]: string;
  };
  workspaceId: string;
  private destroy$ = new Subject<void>();

  constructor(
    private savedViewsDropdownService: SavedViewsDropdownService,
    private savedViewsService: SavedViewsService,
    private siteTableColumnsService: SiteTableColumnsService,
    private preferencesService: PreferencesService,
    private siteTableService: SiteTableService,
    private sitePointFilterService: SitePointFilterService,
    private translationPipe: TranslationPipe,
    private promptService: PromptService,
    private siteOptionsService: SiteOptionsService,
    private customTableService: CustomTableService,
  ) {}

  ngOnInit(): void {
    this.secondaryDropdown = this.ppSecondaryDropdownService.getDropdown();
    this.selectedViewIds = this.savedViewsService.getSelectedViewIds();
    this.workspaceId = checkCustomWorkspaceId() || this.ppViews.personal.workspaceId;
  }

  ngAfterViewInit(): void {
    this.savedViewsService.createInputEvent$
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.focusInput(response);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  focusInput(response: string): void {
    if (response === this.ppView.id) {
      const input = this.nameInput.nativeElement;

      input.parentElement.classList.add('savedViews__focus-border');
      input.contentEditable = 'true';
      this.renamingView = true;
      input.style.cursor = 'text';
      input.focus();
    }
  }

  handleInputBlur($event: FocusEvent): void {
    this.renamingView = false;
    const input = $event.target as HTMLElement;
    input.contentEditable = 'false';
    input.style.cursor = 'pointer';

    this.lastKey !== 'Enter'
      ? this.checkName(input)
      : input.parentElement.classList.remove('savedViews__focus-border');

    this.lastKey = null;
  }

  checkName(input: HTMLElement): void {
    if (input.textContent.trim() === '') {
      this.focusInput(this.ppView.id);
      const prompt = this.translationPipe.transform('prompt_empty_name');
      this.promptService.showWarning(prompt);
    } else {
      input.contentEditable = 'false';
      this.renamingView = false;
      input.style.cursor = 'pointer';
      this.ppView.name = input.textContent;
      this.renameView();
    }
  }

  handleKeyDown($event: KeyboardEvent): void {
    this.lastKey = $event.key;
  }

  handleEnterKeyPress($event: Event): void {
    this.handleKeyDown($event as KeyboardEvent);
    $event.preventDefault();
    const input = $event.target as HTMLElement;
    input.parentElement.classList.remove('savedViews__focus-border');
    this.checkName(input);
  }

  setCursorToEnd(element: HTMLElement): void {
    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false); // Set to the end
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  showDropdown(event: MouseEvent, buttonId: string): void {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.secondaryDropdown.visible && this.secondaryDropdown.buttonId === buttonId) {
      this.dropdownVisible = false;
      this.ppSecondaryDropdownService.hideDropdown();
    } else {
      this.dropdownVisible = true;
      this.ppSecondaryDropdownService.setData<TSavedViewsOptionsDropdownData>({
        view: this.ppView,
        type: this.ppType,
        views: this.ppViews,
      });

      this.ppSecondaryDropdownService.showDropdown(buttonId, SavedViewsOptionsDropdownComponent, {
        secondary: true,
        onClose: () => this.hideDropdown(),
        callback: (option: ESavedViewsOptionsDropdownCallback) => {
          switch (option) {
            case ESavedViewsOptionsDropdownCallback.RENAME:
              this.renamingView = true;
              this.focusInput(this.ppView.id);
              break;
            case ESavedViewsOptionsDropdownCallback.SET_PERSONAL_DEFAULT:
              this.savedViewsDropdownService
                .setPersonalDefaultView(this.workspaceId, this.ppView.id)
                .pipe(
                  tap(() => {
                    this.ppViews.personal.defaultViewId = this.ppView.id;
                  }),
                )
                .subscribe();
              break;
            case ESavedViewsOptionsDropdownCallback.SET_SHARED_DEFAULT:
              this.savedViewsDropdownService
                .setSharedDefaultView(this.workspaceId, this.ppView.id)
                .pipe(
                  tap(() => {
                    this.ppViews.shared.sharedDefaultViewId = this.ppView.id;
                  }),
                )
                .subscribe();

              break;
            case ESavedViewsOptionsDropdownCallback.DUPLICATE:
              this.savedViewsDropdownService.duplicateView(
                this.ppView.id,
                this.workspaceId,
                this.ppType,
              );
              break;
            case ESavedViewsOptionsDropdownCallback.DUPLICATE_TO_SHARED:
              this.savedViewsDropdownService.duplicateView(
                this.ppView.id,
                this.workspaceId,
                ESavedView.SHARED,
              );

              break;
            case ESavedViewsOptionsDropdownCallback.DUPLICATE_TO_PERSONAL:
              this.savedViewsDropdownService.duplicateView(
                this.ppView.id,
                this.workspaceId,
                ESavedView.PERSONAL,
              );
              break;
            case ESavedViewsOptionsDropdownCallback.DELETE:
              this.savedViewsService.deleteView(this.ppView.id, this.workspaceId, this.ppType);

              break;
            case ESavedViewsOptionsDropdownCallback.CLEAR_PERSONAL_DEFAULT:
              this.savedViewsDropdownService
                .setPersonalDefaultView(this.workspaceId, null)
                .pipe(
                  tap(() => {
                    this.ppViews.personal.defaultViewId = null;
                  }),
                )
                .subscribe();

              break;
            case ESavedViewsOptionsDropdownCallback.CLEAR_SHARED_DEFAULT:
              this.savedViewsDropdownService
                .setSharedDefaultView(this.workspaceId, null)
                .pipe(
                  tap(() => {
                    this.ppViews.shared.sharedDefaultViewId = null;
                  }),
                )
                .subscribe();

              break;
          }
        },
        popper: {
          positionFixed: true,
          placement: 'bottom-start',
        },
      });
    }
  }

  hideDropdown(): void {
    this.secondaryDropdown.visible = false;
    this.dropdownVisible = false;
  }

  renameView(): void {
    this.ppView.name = generateViewName(
      this.ppViews,
      this.ppType,
      this.ppView.name,
      this.ppView.id,
    );

    this.renamingView = false;

    this.savedViewsService
      .renameView(this.ppView.id, this.ppView.name, this.workspaceId)
      .subscribe();
  }

  selectView(): void {
    this.savedViewsService.setSelectedViewId(this.workspaceId, this.ppView.id);

    this.savedViewsService
      .fetchView({
        workspaceId: this.workspaceId,
        viewId: this.ppView.id,
      })
      .pipe(
        tap((savedView) => {
          this.siteTableColumnsService.initColumns();

          const savedViews = this.savedViewsService.getSavedViews(this.workspaceId);
          const selectedViewId = this.savedViewsService.getSelectedViewId(this.workspaceId);

          const currentWorkspacePrefs = this.preferencesService.getCurrentWorkspaceLayout({
            savedViews,
            selectedViewId,
          });

          this.siteTableService.processPreferencesResponse(currentWorkspacePrefs, this.workspaceId);
          this.siteOptionsService.getGroupButton().update();
          this.siteOptionsService.getSortButton().update();
          this.sitePointFilterService.filterPointsFromSavedView({
            _keepScrollPosition: false,
          });

          this.preferencesService.getCurrentWorkspaceLayout({
            savedViews,
            selectedViewId,
          });

          this.customTableService.reloadTable();

          logEventInGTAG(EGoogleEventSavedViews.APPLY_SAVED_VIEW, {
            source: EGoogleEventCategory.SAVED_VIEWS,
            type: savedView.viewType,
          });
        }),
      )
      .subscribe();
  }
}
